import {
  PROTOCOL_VERSION,
  decodeSnapshot,
  parseControlMessage,
  type InputMessage,
  type PingMessage,
  type PongMessage,
  type RaceStateMessage,
  type Snapshot,
  type WelcomeMessage,
} from "@cimara/protocol";

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "closed";

export interface CimaraSocketCallbacks {
  onStatus(state: ConnectionState, detail: string): void;
  onWelcome(message: WelcomeMessage): void;
  onSnapshot(snapshot: Snapshot, receivedAt: number): void;
  onError(message: string): void;
  onRaceState?(message: RaceStateMessage): void;
}

export interface CimaraSocketOptions {
  token?: string;
  /**
   * Fetched before every hello, including reconnects. A signed-in rider needs
   * a fresh five-minute `/api/ws-token`; a guest needs a guest token.
   */
  tokenProvider?: () => Promise<string>;
  clientVersion?: string;
  url?: string;
  callbacks: CimaraSocketCallbacks;
}

const RIDER_STORAGE_KEY = "cimara.riderId";
const PING_INTERVAL_MS = 5000;

export function defaultSocketUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
}

function readStoredRiderId(): number | undefined {
  try {
    const raw = window.sessionStorage.getItem(RIDER_STORAGE_KEY);
    if (!raw) return undefined;
    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function storeRiderId(riderId: number): void {
  try {
    window.sessionStorage.setItem(RIDER_STORAGE_KEY, String(riderId));
  } catch {
    // Session storage may be blocked; reconnect simply starts a fresh rider.
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * One rider's WebSocket. JSON control messages, binary snapshots, reconnect
 * with the previous rider id, and no position in the outbound direction.
 */
export class CimaraSocket {
  private readonly callbacks: CimaraSocketCallbacks;
  private readonly token: string;
  private readonly tokenProvider: (() => Promise<string>) | null;
  private readonly clientVersion: string;
  private readonly url: string;

  private socket: WebSocket | null = null;
  private riderId: number | undefined;
  private welcomed = false;
  private shouldReconnect = true;
  private fatal = false;
  private attempt = 0;
  private seq = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private lastRttMs: number | null = null;

  constructor(options: CimaraSocketOptions) {
    this.callbacks = options.callbacks;
    this.token = options.token ?? "cimara-dev";
    this.tokenProvider = options.tokenProvider ?? null;
    this.clientVersion = options.clientVersion ?? "0.1.0";
    this.url = options.url ?? defaultSocketUrl();
    this.riderId = readStoredRiderId();
  }

  getSocketUrl(): string {
    return this.url;
  }

  getRttMs(): number | null {
    return this.lastRttMs;
  }

  connect(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    this.shouldReconnect = true;
    if (this.fatal) return;

    this.clearReconnectTimer();
    this.welcomed = false;
    this.callbacks.onStatus(
      this.attempt === 0 ? "connecting" : "reconnecting",
      this.attempt === 0 ? "Connecting to Cimara" : `Reconnect attempt ${this.attempt}`,
    );

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch (error) {
      this.callbacks.onError(`Could not open the socket: ${errorMessage(error)}`);
      this.scheduleReconnect();
      return;
    }
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.callbacks.onStatus("connecting", "Negotiating rider");
      void this.sendHello();
      this.startPing();
    });

    socket.addEventListener("message", (event) => {
      if (this.socket !== socket) return;
      void this.handleMessage(event.data);
    });

    socket.addEventListener("error", () => {
      // The close event follows and owns the retry decision. This handler only
      // exists so a transport error never becomes an unhandled event.
      if (this.socket === socket && !this.shouldReconnect) {
        this.callbacks.onStatus("error", "The server connection failed");
      }
    });

    socket.addEventListener("close", (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.welcomed = false;
      this.stopPing();

      if (!this.shouldReconnect) {
        this.callbacks.onStatus("closed", "Ride ended");
        return;
      }
      if (this.fatal) {
        this.callbacks.onStatus(
          "error",
          event.reason || "The server rejected the connection",
        );
        return;
      }
      this.attempt += 1;
      this.callbacks.onStatus(
        "reconnecting",
        event.code === 1006 ? "Server unavailable — local preview" : "Connection lost — reconnecting",
      );
      this.scheduleReconnect();
    });
  }

  sendInput(watts: number, cadence: number): void {
    if (!this.welcomed || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.seq += 1;
    const message: InputMessage = {
      type: "input",
      seq: this.seq,
      t: Date.now(),
      watts: Math.round(clamp(watts, 0, 2500)),
      cadence: Math.round(clamp(cadence, 0, 250)),
    };
    try {
      this.socket.send(JSON.stringify(message));
    } catch (error) {
      this.callbacks.onError(`Could not send input: ${errorMessage(error)}`);
    }
  }

  close(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.stopPing();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close(1000, "ride ended");
      } catch {
        // Already closed.
      }
    }
    this.callbacks.onStatus("closed", "Ride ended");
  }

  private async sendHello(): Promise<void> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    let token = this.token;
    if (this.tokenProvider) {
      try {
        token = await this.tokenProvider();
      } catch (error) {
        this.fatal = true;
        this.shouldReconnect = false;
        this.callbacks.onError(`Could not get a ride token: ${errorMessage(error)}`);
        this.callbacks.onStatus("error", "Ride token unavailable");
        const socket = this.socket;
        this.socket = null;
        try {
          socket.close(1008, "token unavailable");
        } catch {
          // Already closed.
        }
        return;
      }
    }

    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(
      JSON.stringify({
        type: "hello",
        token,
        protocolVersion: PROTOCOL_VERSION,
        clientVersion: this.clientVersion,
        ...(this.riderId === undefined ? {} : { riderId: this.riderId }),
      }),
    );
  }

  private async handleMessage(data: unknown): Promise<void> {
    if (typeof data === "string") {
      this.handleControl(data);
      return;
    }

    let buffer: ArrayBuffer | null = null;
    if (data instanceof ArrayBuffer) {
      buffer = data;
    } else if (data instanceof Blob) {
      buffer = await data.arrayBuffer();
    }

    if (!buffer) return;

    try {
      const snapshot = decodeSnapshot(buffer);
      this.callbacks.onSnapshot(snapshot, Date.now());
    } catch (error) {
      this.callbacks.onError(`Ignored a malformed snapshot: ${errorMessage(error)}`);
    }
  }

  private handleControl(text: string): void {
    let message;
    try {
      message = parseControlMessage(text);
    } catch (error) {
      this.callbacks.onError(`Ignored a malformed server message: ${errorMessage(error)}`);
      return;
    }

    switch (message.type) {
      case "welcome": {
        this.welcomed = true;
        this.riderId = message.riderId;
        storeRiderId(message.riderId);
        this.attempt = 0;
        this.callbacks.onWelcome(message);
        this.callbacks.onStatus("connected", "Live");
        break;
      }
      case "ping": {
        const pong: PongMessage = { type: "pong", t: message.t };
        this.sendControl(pong);
        break;
      }
      case "pong": {
        this.lastRttMs = Math.max(0, Date.now() - message.t);
        break;
      }
      case "race_state": {
        this.callbacks.onRaceState?.(message);
        break;
      }
      case "error": {
        if (
          message.code === "bad_token" ||
          message.code === "protocol_version" ||
          message.code === "already_authenticated" ||
          message.code === "already_connected"
        ) {
          this.fatal = true;
          this.shouldReconnect = false;
        }
        this.callbacks.onError(message.message);
        this.callbacks.onStatus("error", message.message);
        break;
      }
      default:
        break;
    }
  }

  private sendControl(message: PingMessage | PongMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify(message));
    } catch {
      // A missed pong is harmless.
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.fatal || this.reconnectTimer !== null) return;
    const delay = Math.min(5000, 750 * 2 ** Math.min(this.attempt, 3));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      this.sendControl({ type: "ping", t: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
